import { useMemo, useState } from 'react'
import { useAuroraStore } from '../../store/auroraStore'
import {
  materialKindCounts,
  materialSkillCounts,
  materialSkillIds,
  materialKind,
  materials,
} from '../../store/selectors'
import { kindLabel } from '../../lib/specs'
import { MaterialCard } from './MaterialCard'

/**
 * Everything made so far, filtered by what it is and what it works on.
 *
 * Two axes because those are the two real questions: "where is that counting
 * game" and "what do I have for her reading". Both filter lists are built from
 * what is actually in the library, so a kind or skill nobody has yet never
 * appears as an empty option.
 */
export function LibraryGrid({
  loading = false,
  onPrint,
}: {
  loading?: boolean
  onPrint?: (artifactId: string) => void
}) {
  const all = useAuroraStore(materials)
  const kinds = useAuroraStore(materialKindCounts)
  const skills = useAuroraStore(materialSkillCounts)

  const [kind, setKind] = useState<string | null>(null)
  const [skill, setSkill] = useState<string | null>(null)

  const shown = useMemo(
    () =>
      all.filter((m: any) => {
        if (kind && materialKind(m) !== kind) return false
        if (skill && !materialSkillIds(m).includes(skill)) return false
        return true
      }),
    [all, kind, skill],
  )

  if (loading && all.length === 0) {
    return (
      <div className="mat-grid" aria-busy="true" aria-label="Loading materials">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="mat mat-skeleton">
            <div className="skeleton" style={{ height: 64 }} />
            <div className="skeleton" style={{ height: 14, width: '70%' }} />
            <div className="skeleton" style={{ height: 12, width: '45%' }} />
          </div>
        ))}
      </div>
    )
  }

  if (all.length === 0) {
    return (
      <div className="empty">
        <p className="empty-title">The shelf is empty</p>
        <p className="empty-body">
          Everything here gets built around what she is working on right now. Describe one thing
          you want and it will be waiting the next time you open this.
        </p>
      </div>
    )
  }

  const filtering = kind !== null || skill !== null

  return (
    <>
      <div className="chips" role="group" aria-label="Filter by activity type">
        <button
          className={`chip${kind === null ? ' is-active' : ''}`}
          onClick={() => setKind(null)}
          aria-pressed={kind === null}
        >
          All types <span className="chip-count tnum">{all.length}</span>
        </button>
        {kinds.map(({ kind: k, count }) => (
          <button
            key={k}
            className={`chip${kind === k ? ' is-active' : ''}`}
            onClick={() => setKind(kind === k ? null : k)}
            aria-pressed={kind === k}
          >
            {kindLabel(k)} <span className="chip-count tnum">{count}</span>
          </button>
        ))}
      </div>

      {skills.length > 0 && (
        <div className="chips" role="group" aria-label="Filter by skill">
          <button
            className={`chip chip-quiet${skill === null ? ' is-active' : ''}`}
            onClick={() => setSkill(null)}
            aria-pressed={skill === null}
          >
            Every skill
          </button>
          {skills.map(({ id, label, count }) => (
            <button
              key={id}
              className={`chip chip-quiet${skill === id ? ' is-active' : ''}`}
              onClick={() => setSkill(skill === id ? null : id)}
              aria-pressed={skill === id}
            >
              {label} <span className="chip-count tnum">{count}</span>
            </button>
          ))}
        </div>
      )}

      {shown.length === 0 ? (
        <div className="empty">
          <p className="empty-title">Nothing matches those two filters</p>
          <p className="empty-body">
            There is plenty in the library — just not this combination yet.
          </p>
          <button
            className="btn btn-ghost btn-sm"
            style={{ marginTop: 'var(--s-4)' }}
            onClick={() => {
              setKind(null)
              setSkill(null)
            }}
          >
            Show everything
          </button>
        </div>
      ) : (
        <>
          {filtering && (
            <p className="mat-grid-count tnum">
              {shown.length} of {all.length}
            </p>
          )}
          <div className="mat-grid">
            {shown.map((m: any, i: number) => (
              <div key={m._id} className="reveal" style={{ '--i': i } as React.CSSProperties}>
                <MaterialCard artifact={m} onPrint={onPrint} />
              </div>
            ))}
          </div>
        </>
      )}
    </>
  )
}
